import React, { useState } from 'react';

import Header from './Header';
import Footer from './Footer';

import '../App.css';
import './VolunteerForm.css';

const SKILL_OPTIONS = [
  'Research & fact-checking',
  'Translation',
  'Online investigation (OSINT)',
  'Media monitoring',
  'Writing & editing',
  'Tech & development',
];

const LANGUAGE_OPTIONS = [
  'English', 'Arabic', 'Hebrew', 'French', 'Spanish', 'German', 'Italian', 'Portuguese',
];

const GetInTouchForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    is_volunteer: false,
    skills: [],
    languages: [],
    otherLanguage: '',
    extra_info: '',
    newsletter: false,
    _hp: '', // honeypot
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Name and email are required.');
      return;
    }
    if (!formData.message.trim()) {
      setError('Please enter a message.');
      return;
    }

    // Build languages array — include "other" text if provided
    const languages = [...formData.languages];
    if (formData.otherLanguage.trim()) {
      languages.push(formData.otherLanguage.trim());
    }

    const payload = {
      source: formData.is_volunteer ? 'volunteer' : 'contact',
      name: formData.name.trim(),
      email: formData.email.trim(),
      is_volunteer: formData.is_volunteer,
      message: formData.message.trim(),
      skills: formData.is_volunteer ? formData.skills : [],
      languages: formData.is_volunteer ? languages : [],
      extra_info: formData.is_volunteer ? formData.extra_info.trim() : '',
      newsletter: formData.newsletter,
      _hp: formData._hp, // honeypot
    };

    try {
      setIsLoading(true);
      const response = await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
        sessionStorage.setItem('hasSubmitted_contact', 'true');
      } else {
        setError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Could not connect. Please check your internet and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Thank-you state ---
  if (isSubmitted) {
    return (
      <>
        <Header />
        <div className="content-container">
          <h2 className="subheading">Thank you</h2>
          <div className="home-text">
            {formData.is_volunteer
              ? "Thanks for signing up to volunteer. We'll get back to you as soon as we can."
              : "Thanks for your message. We'll get back to you as soon as we can."}
          </div>
          <br />
          <div className="home-text">Til liberation 🍉</div>
        </div>
        <Footer />
      </>
    );
  }

  // --- Form ---
  return (
    <>
      <Header />
      <div className="content-container">
        <h2 className="subheading">Get in touch</h2>
        <div className="home-text">
          Leave your message below if you'd like to get in touch with the Hasbara Tracker team.
          You can also sign up to volunteer.
        </div>
        <br />

        {error && (
          <div role="alert" style={{ color: '#be1515', marginBottom: '1rem', fontWeight: 'bold' }}>
            {error}
          </div>
        )}

        <form className="mt-5" onSubmit={handleSubmit}>
          {/* Honeypot — hidden from humans */}
          <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
            <input
              type="text"
              name="_hp"
              value={formData._hp}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Name */}
          <label className="font-semibold">
            Name <span className="required-field">*</span>
            <input
              className="w-full p-1 mt-[2.5%] mb-9"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              maxLength={200}
            />
          </label>
          <br />

          {/* Email */}
          <label className="font-semibold">
            Email address <span className="required-field">*</span>
            <input
              className="w-full p-1 mt-[2.5%] mb-9"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              maxLength={254}
            />
          </label>
          <br />

          {/* Message */}
          <label className="font-semibold">
            Your message <span className="required-field">*</span>
            <textarea
              className="w-full p-5 mt-[2.5%] mb-9"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              maxLength={5000}
              rows={6}
            />
          </label>
          <br />

          {/* Volunteer toggle */}
          <label className="flex items-start gap-3 mb-6">
            <input
              type="checkbox"
              name="is_volunteer"
              checked={formData.is_volunteer}
              onChange={handleChange}
              className="mt-1 min-w-4 h-4"
            />
            <span>
              <b>I'd like to volunteer</b><br />
              <span className="text-sm">
                Help us track and debunk Israeli state propaganda. Anyone can contribute —
                especially if you have a propensity for research and collecting receipts.
              </span>
            </span>
          </label>

          {/* --- Volunteer fields (shown when toggled) --- */}
          {formData.is_volunteer && (
            <div style={{ borderLeft: '3px solid #bffb9b', paddingLeft: '1.5rem', marginBottom: '1.5rem' }}>
              {/* Skills multi-select */}
              <label>
                <b>Skills</b>
                <span className="text-sm" style={{ fontWeight: 'normal' }}> — select all that apply</span>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-[2.5%] mb-6 text-sm">
                  {SKILL_OPTIONS.map(skill => (
                    <label key={skill} className="flex items-start gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={formData.skills.includes(skill)}
                        onChange={() => handleMultiSelect('skills', skill)}
                        className="mt-0.5 min-w-4 h-4"
                      />
                      <span className="text-sm">{skill}</span>
                    </label>
                  ))}
                </div>
              </label>

              {/* Languages multi-select */}
              <label>
                <b>Languages</b>
                <span className="text-sm" style={{ fontWeight: 'normal' }}> — select all that apply</span>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-[2.5%] mb-4 text-sm">
                  {LANGUAGE_OPTIONS.map(lang => (
                    <label key={lang} className="flex items-start gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={formData.languages.includes(lang)}
                        onChange={() => handleMultiSelect('languages', lang)}
                        className="mt-0.5 min-w-4 h-4"
                      />
                      <span className="text-sm">{lang}</span>
                    </label>
                  ))}
                </div>
                <div className="mb-6">
                  <input
                    type="text"
                    name="otherLanguage"
                    value={formData.otherLanguage}
                    onChange={handleChange}
                    className="w-full p-2 mt-1"
                    maxLength={50}
                    placeholder="Other language(s)"
                  />
                </div>
              </label>

              {/* Extra info */}
              <label>
                <b>Anything else?</b>
                <span className="text-sm" style={{ fontWeight: 'normal' }}> — availability, background, how you'd like to help</span>
                <textarea
                  className="w-full p-5 mt-[2.5%] mb-6"
                  name="extra_info"
                  value={formData.extra_info}
                  onChange={handleChange}
                  maxLength={2000}
                  rows={4}
                />
              </label>
            </div>
          )}

          {/* Newsletter */}
          <label className="flex items-start gap-3 mb-6">
            <input
              type="checkbox"
              name="newsletter"
              checked={formData.newsletter}
              onChange={handleChange}
              className="mt-1 min-w-4 h-4"
            />
            <span className="text-sm">
              <b>Sign up for news and updates from Hasbara Tracker</b><br />
              Get claims straight to your inbox. We'll use the name and email you entered above.
            </span>
          </label>

          {/* Submit */}
          {isLoading ? (
            <p>Submitting...</p>
          ) : (
            <button className="btn-green" type="submit">
              Submit
            </button>
          )}
        </form>
      </div>
      <Footer />
    </>
  );
};

export default GetInTouchForm;
